import { ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { logout } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FlaskConical,
  FileSearch,
  Package,
  PackageCheck,
  PackagePlus,
  ShoppingBag,
  Route,
  Factory,
  UserRoundCog,
  Info,
  LogOut,
  X,
} from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

function Layout({ children }: LayoutProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { ppsAccount } = useAppSelector((state) => state.auth);
  const [showAbout, setShowAbout] = useState(false);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const mainCharacter = ppsAccount?.mainCharacter;

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/build-tree', label: 'Build Tree', icon: FlaskConical },
    { path: '/blueprints', label: 'Blueprints', icon: FileSearch },
    { path: '/sales-orders', label: 'Sales Orders', icon: ShoppingBag },
    { path: '/supply-chain', label: 'Supply Chain', icon: Route },
    { path: '/assets', label: 'Assets', icon: Package },
    { path: '/industry-jobs', label: 'Industry Jobs', icon: PackageCheck },
    { path: '/buy-orders', label: 'Buy Orders', icon: PackagePlus },
    { path: '/structures', label: 'Structures', icon: Factory },
    { path: '/settings', label: 'Settings', icon: UserRoundCog },
  ];

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Sidebar */}
      <aside className="w-72 bg-gray-800 border-r border-gray-700 flex flex-col">
        {/* Title */}
        <div className="p-6 border-b border-gray-700">
          <img src="/EVEPPS_Logo.png" alt="EVE PPS" className="mx-auto mb-2 rounded-lg shadow-lg" />
          <h1 className="text-lg font-bold text-white text-center">Production Planning System</h1>
        </div>

        {/* Main Character */}
        {mainCharacter && (
          <div className="p-4 border-b border-gray-700">
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <img
                  src={`https://images.evetech.net/characters/${mainCharacter.characterId}/portrait?size=64`}
                  alt={mainCharacter.characterName}
                  className="w-12 h-12 rounded-lg border-2 border-blue-500"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">
                    {mainCharacter.characterName}
                  </p>
                  {mainCharacter.corporationId && mainCharacter.corporationName && (
                    <div className="flex items-center space-x-1 mt-1">
                      <img
                        src={`https://images.evetech.net/corporations/${mainCharacter.corporationId}/logo?size=32`}
                        alt={mainCharacter.corporationName}
                        className="w-4 h-4 rounded"
                      />
                      <p className="text-xs text-gray-400 truncate">
                        {mainCharacter.corporationName}
                      </p>
                    </div>
                  )}
                  {mainCharacter.allianceId && mainCharacter.allianceName && (
                    <div className="flex items-center space-x-1 mt-0.5">
                      <img
                        src={`https://images.evetech.net/alliances/${mainCharacter.allianceId}/logo?size=32`}
                        alt={mainCharacter.allianceName}
                        className="w-4 h-4 rounded"
                      />
                      <p className="text-xs text-gray-400 truncate">
                        {mainCharacter.allianceName}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`
              }
            >
              <item.icon className="w-5 h-5 text-white" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* About & Logout */}
        <div className="p-4 border-t border-gray-700 space-y-2">
          <button
            onClick={() => setShowAbout(true)}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
          >
            <Info className="w-5 h-5" />
            <span className="font-medium">About EVE PPS</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* About Modal */}
      {showAbout && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">About EVE PPS</h2>
              <button
                onClick={() => setShowAbout(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-300">
                Thank you for using EVE PPS. This is still a hobby project and is undergoing continuous development. Feedback is always welcome :)
              </p>

              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-gray-400 text-sm mb-2">Contact</p>
                <p className="text-gray-200">
                  In-game: <span className="text-blue-400">Jari Tzunlight</span>
                </p>
                <p className="text-gray-200">
                  zKillboard:{' '}
                  <a
                    href="https://zkillboard.com/character/1159761077/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    https://zkillboard.com/character/1159761077/
                  </a>
                </p>
              </div>

              <div className="text-xs text-gray-500 leading-relaxed pt-4 border-t border-gray-700">
                <p>
                  EVE Online, the EVE logo, EVE and all associated logos and designs are the intellectual property of CCP hf. All artwork, screenshots, characters, vehicles, storylines, world facts or other recognizable features of the intellectual property relating to these trademarks are likewise the intellectual property of CCP hf. EVE Online and the EVE logo are the registered trademarks of CCP hf. All rights are reserved worldwide. All other trademarks are the property of their respective owners. CCP is in no way responsible for the content on or functioning of this website, nor can it be liable for any damage arising from the use of this website.
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700">
              <button
                onClick={() => setShowAbout(false)}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        {children}
      </main>
    </div>
  );
}

export default Layout;
